import React, {useCallback, useState} from 'react';
import {IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Typography} from '@mui/material';
import {ArrowBack, AutoAwesome, ChevronRight} from '@mui/icons-material';
import GenerateInitialVersionView from './GenerateInitialVersionView';
import useGenerateInitialVersionStore from '../../../../stores/useGenerateInitialVersionStore';
import {useLeftPanelSync} from '../../hooks/useLeftPanelSync';

/**
 * AI Assistant for Disclosure Editor
 * Simplified version with disclosure-specific AI capabilities
 *
 * The Generate Initial Version modal is now managed globally via Zustand store.
 * This allows it to be triggered from multiple places (header, dialog, etc.)
 */
const DisclosureAIAssistant = () => {
    const {aiView, setAiView} = useLeftPanelSync();

    // Form state (local to this view)
    const [instructions, setInstructions] = useState('');
    const [selectedPolicy, setSelectedPolicy] = useState('default');

    // Get openModal from the global store
    const openModal = useGenerateInitialVersionStore(s => s.openModal);

    const handleViewClick = useCallback((viewKey) => {
        console.log('🤖 Disclosure AI Assistant view click:', {viewKey});
        setAiView(viewKey);
    }, [setAiView]);

    const handleBack = useCallback(() => {
        console.log('⬅️ Disclosure AI Assistant back to main menu');
        setAiView(null);
    }, [setAiView]);

    const handleInstructionsChange = useCallback((event) => {
        setInstructions(event.target.value);
    }, []);

    const handlePolicyChange = useCallback((newPolicy) => {
        setSelectedPolicy(newPolicy);
    }, []);

    const handleGenerate = useCallback(() => {
        console.log('🚀 Generate initial version:', {instructions, selectedPolicy});
        // Open the global modal with current instructions and policy
        openModal({
            instructions,
            selectedPolicy
        });
    }, [instructions, selectedPolicy, openModal]);

    // Show Generate Initial Version view
    if (aiView === 'generate_initial') {
        return (
            <Stack bgcolor={'white'} flexGrow={1} overflow={'hidden'}>
                <Stack
                    direction="row"
                    alignItems="center"
                    px={1}
                    py={1}
                    bgcolor="grey.50"
                    borderBottom={1}
                    borderColor="divider"
                >
                    <IconButton size="small" onClick={handleBack}>
                        <ArrowBack fontSize="small"/>
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight="medium" ml={1}>
                        Generate initial version
                    </Typography>
                </Stack>
                <GenerateInitialVersionView
                    instructions={instructions}
                    onInstructionsChange={handleInstructionsChange}
                    selectedPolicy={selectedPolicy}
                    onPolicyChange={handlePolicyChange}
                    onGenerate={handleGenerate}
                />
            </Stack>
        );
    }

    // Show main menu
    return (
        <Stack height="100%" bgcolor={'white'}>
            <List sx={{flex: 1, overflow: 'auto'}}>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => handleViewClick('generate_initial')}>
                        <ListItemIcon>
                            <AutoAwesome/>
                        </ListItemIcon>
                        <ListItemText
                            primary="Generate initial version"
                            secondary="Create initial disclosure from source document"
                        />
                        <ChevronRight color="action"/>
                    </ListItemButton>
                </ListItem>
            </List>
        </Stack>
    );
};

export default DisclosureAIAssistant;
